import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "hooks/redux";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { IMergedReportEntry, TReportAdminScope } from "pages/secure/shared/reports/reportCatalog.types";
import {
  isQuickExportParamsFailure,
  isQuickExportRunFailure,
  TQuickExportParamsResult,
  TQuickExportRunResult,
} from "pages/secure/shared/reports/quickExport/quickExport.types";
import { buildQuickExportParams } from "pages/secure/shared/reports/quickExport/buildQuickExportParams";
import { runQuickExport } from "pages/secure/shared/reports/quickExport/runQuickExport";

interface IUseQuickReportExportOptions {
  scope: TReportAdminScope;
  dedicatedRouteCodes: ReadonlySet<string>;
}

export function useQuickReportExport(options: IUseQuickReportExportOptions) {
  const { scope, dedicatedRouteCodes } = options;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isExportingRef = useRef(false);

  const quickExport = useCallback(
    async (report: IMergedReportEntry) => {
      if (isExportingRef.current) {
        return;
      }

      isExportingRef.current = true;
      dispatch(enqueue());

      const paramsResult: TQuickExportParamsResult = await buildQuickExportParams({
        scope,
        reportId: report.id,
        code: report.code,
        isDedicatedRoute: dedicatedRouteCodes.has(report.code),
      });

      if (isQuickExportParamsFailure(paramsResult)) {
        dispatch(dequeue());
        dispatch(showError(paramsResult.errorMessage));
        isExportingRef.current = false;
        return;
      }

      const exportResult: TQuickExportRunResult = await runQuickExport(scope, paramsResult.params, report.title);

      dispatch(dequeue());
      isExportingRef.current = false;

      if (isQuickExportRunFailure(exportResult)) {
        dispatch(showError(exportResult.errorMessage));
        return;
      }

      dispatch(showSuccess(t("Report exported successfully")));
    },
    [dedicatedRouteCodes, dispatch, scope, t],
  );

  return {
    quickExport,
  };
}
